import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import ModalWrapper from "components/common/ModalWrapper";
import LoadingUI from "components/common/LoadingUI";
import HighLightRowWrapper from "components/common/HighLightRowWrapper";
import HighchartWrapper from "components/common/HighchartWrapper";
import DateTimeFormatter from "components/common/DateTimeFormatter";
import StickyTableWrapper from "components/common/StickyTableWrapper";

import NotesInformation from "./NotesInformation";
import ConfigDiffView from "./ConfigDiffView";
import ClickToCopyButton from "./ClickToCopyButton";

import { getDateTimeLabel } from "helpers/Chart";
import { getDateList } from "helpers/DateTransform";
import { CHANGETYPE } from "helpers/Compare";
import { tdClass, thClass } from "helpers/StyleClass";
import { FaTimes } from "react-icons/fa";

const buttonActionClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-400 whitespace-pre shadow hover:bg-gray-300 text-gray-900 font-semibold";

const buttonCloseClass =
  "px-2 h-6 bg-gray-100 text-xs whitespace-pre hover:text-blue-600 border border-r-0 text-gray-900 font-semibold";

const buttonClassBlue =
  "px-2 py-1 bg-blue-200 text-xs rounded border border-gray-400 hover:bg-blue-300 text-gray-900 font-semibold";

const FETCH_SIZE = [100, 50, 25, 10];
const PAGE_SIZE = 10;

function getDiffLength(diffs, isFirstLook = false) {
  if (isFirstLook) {
    return _.sumBy(_.values(diffs), "length");
  } else {
    return diffs.length;
  }
}

class ChangeLogModal extends React.Component {
  _isMounted = true;

  constructor(props) {
    super(props);
    this.state = {
      errmsg: "",
      isLoading: false,

      isSpecificRule: false,
      changes: [],
      filteredChanges: [],
      currentFetchSize: FETCH_SIZE[0],
      endCursor: null,
      hasNextPage: true,
      lastTime: moment().toISOString(),
      isFetching: true,

      chartOptions: null,

      selectedId: "",
      isOpenView: false,
    };

    this.handleScroll = this.handleScroll.bind(this);
    this.handleFetchData = this.handleFetchData.bind(this);
    this.handleFetchWithFallback = this.handleFetchWithFallback.bind(this);
    this.handleClickDateLabel = this.handleClickDateLabel.bind(this);
    this.handleReplaceData = this.handleReplaceData.bind(this);
  }

  async componentDidMount() {
    const { fetchData, currentRule } = this.props;

    if (!fetchData) return;

    this._isMounted = true;
    this.setState({ isSpecificRule: !_.isNil(currentRule) }, () => {
      this.handleFetchData();
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async handleFetchData() {
    const {
      networkId,
      fetchData,
      compareData,
      currentRule,
      dataKey = "config",
    } = this.props;

    if (!fetchData || !compareData) return;

    try {
      this.setState({ errmsg: "", isLoading: true });
      let hasNextPage = this.state.hasNextPage;
      let endCursor = this.state.endCursor;

      const list = [...this.state.changes];
      const collected = [];

      function isContinueFetch(collected, isSpecificRule, isFirstLook = false) {
        if (isSpecificRule) {
          const isAdd = _.some(collected, ({ diffs }) =>
            _.some(
              diffs,
              ({ type, from, path }) =>
                type === CHANGETYPE.ARRAY_ADDED &&
                _.isArray(from) &&
                from.length === 0 &&
                /^\[id:(\d+)\]$/.test(path || "")
            )
          );

          if (isAdd) return false;

          return (
            collected.filter(
              (change) => getDiffLength(change.diffs, isFirstLook) > 0
            ).length < PAGE_SIZE
          );
        } else {
          return collected.length < PAGE_SIZE;
        }
      }

      while (
        this.state.isFetching &&
        hasNextPage &&
        isContinueFetch(
          collected,
          this.state.isSpecificRule,
          this.props.isFirstLook
        ) &&
        this._isMounted
      ) {
        const apiResult = await this.handleFetchWithFallback({
          networkId,
          endCursor,
        });

        if (!this._isMounted) return;
        if (_.isNil(apiResult)) {
          this.setState({ isLoading: false, isFetching: false });
          return;
        }

        const { edges, pageInfo } = apiResult;

        hasNextPage = _.get(pageInfo, "hasNextPage", false);
        endCursor = _.get(pageInfo, "endCursor", null);

        const result = _.map(edges, ({ node, cursor }) => {
          let config = node[dataKey] || [];
          if (this.state.isSpecificRule) {
            if (!_.isNil(this.props.currentType)) {
              config = {
                [this.props.currentType]: _.get(
                  config,
                  this.props.currentType,
                  []
                ).filter((item) => item.id === currentRule.id),
              };
            } else {
              config = _.filter(config, (item) => item.id === currentRule.id);
            }
          }

          return {
            ...node,
            cursor,
            config,
            diffs: [],
            compareIdx: -1,
            createdDate: moment(node.createdAt).format("YYYY/MM/DD"),
          };
        });

        const sorted = _.orderBy(result, ["createdAt"], ["desc"]);

        const newList = [...list, ...collected];
        if (newList.length > 0) {
          const lastChange = newList[newList.length - 1];
          const updatedLastChange = {
            ...lastChange,
            diffs: compareData(sorted[0].config, lastChange.config),
            compareIdx: newList.length,
          };
          if (collected.length > 0) {
            collected[collected.length - 1] = updatedLastChange;
          } else {
            list[list.length - 1] = updatedLastChange;
          }
        }

        _.forEach(sorted, (change, idx) => {
          if (idx !== sorted.length - 1) {
            change.diffs = compareData(sorted[idx + 1].config, change.config);
            change.compareIdx = idx + 1 + newList.length;
          }
        });

        collected.push(...sorted);

        this.setState({
          lastTime: _.get(collected, [collected.length - 1, "createdAt"], null),
        });
      }

      // Initial load
      const stateData = {};
      if (this.state.selectedId === "" && collected.length > 0) {
        if (this.state.isSpecificRule) {
          const firstChange = _.find(
            collected,
            (change) => getDiffLength(change.diffs, this.props.isFirstLook) > 0
          );
          if (firstChange) {
            stateData.selectedId = firstChange.historyId || "";
          }
        } else {
          stateData.selectedId = _.get(collected, [0, "historyId"], "");
        }
      }

      const changes = [...list, ...collected];
      const filteredChanges = this.state.isSpecificRule
        ? changes.filter((change) =>
            _.isNil(this.props.currentType)
              ? change.diffs.length > 0
              : (change.diffs[this.props.currentType] || []).length > 0
          )
        : changes;

      if (this.state.isSpecificRule) {
        stateData.chartOptions = this.parseChartOptions(filteredChanges);
      }

      this.setState({
        changes,
        filteredChanges,
        endCursor,
        hasNextPage,
        ...stateData,
      });
    } catch (error) {
      this.setState({
        errmsg: error.toString(),
      });
    }

    this.setState({ isLoading: false, isFetching: false });
  }

  async handleFetchWithFallback({ networkId, endCursor }) {
    const { currentFetchSize } = this.state;
    const startIndex = FETCH_SIZE.indexOf(currentFetchSize);
    const sizes = startIndex === -1 ? FETCH_SIZE : FETCH_SIZE.slice(startIndex);

    for (let i = 0; i < sizes.length; i++) {
      const size = sizes[i];

      if (this._isMounted && this.state.currentFetchSize !== size) {
        this.setState({ currentFetchSize: size });
      }

      try {
        if (!this._isMounted || !this.state.isFetching) {
          return null;
        }

        const res = await this.props.fetchData({
          networkId,
          first: size,
          after: endCursor,
        });

        return res;
      } catch (err) {
        if (i === sizes.length - 1) {
          throw err;
        }
      }
    }
  }

  handleScroll() {
    const { hasNextPage, isLoading } = this.state;
    if (isLoading || !hasNextPage) return;

    this.setState({ isFetching: true }, () => {
      this.handleFetchData();
    });
  }

  handleClickDateLabel(date) {
    const { filteredChanges } = this.state;
    const firstMatch = filteredChanges.find((change) =>
      change.createdDate.startsWith(date)
    );

    if (!firstMatch) return;

    const selectedId = _.get(firstMatch, "historyId", "");
    this.setState({ selectedId });

    const element = document.getElementById(selectedId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  handleReplaceData(selected) {
    const { isSpecificRule } = this.state;
    this.props.handleReplaceData({
      ...selected,
      isSpecificRule,
    });
  }

  parseChartOptions(config) {
    const fromDate = _.get(config, [config.length - 1, "createdDate"]);
    const toDate = _.get(config, [0, "createdDate"]);
    if (_.isEmpty(fromDate) || _.isEmpty(toDate)) return null;

    const dateList = getDateList(fromDate, toDate, true).map((d) =>
      d.replace(/-/g, "/")
    );
    const dateIndexMap = _.fromPairs(dateList.map((d, i) => [d, i]));

    const groupedConfig = _.groupBy(config, "createdDate");
    const groupedCount = _.mapValues(groupedConfig, (list) => list.length);

    const chartOptions = {
      chart: {
        type: "bubble",
        height: 110,
        spacingTop: 0,
        spacingBottom: 8,
        scrollablePlotArea: {
          minWidth: dateList.length * 40,
          scrollPositionX: 0,
          opacity: 0,
        },
      },
      title: {
        text: null,
      },
      xAxis: {
        categories: dateList,
        lineWidth: 0,
        min: 0,
        max: dateList.length - 1,
        labels: {
          y: 0,
          formatter: function () {
            const m = moment(this.value);
            return getDateTimeLabel(m, this.isFirst);
          },
        },
      },
      yAxis: {
        min: -0.5,
        max: 0.5,
        gridLineWidth: 0,
        title: null,
        labels: { enabled: false },
        plotLines: [
          {
            value: 0,
            color: "#ccc",
            width: 1,
          },
        ],
      },
      legend: { enabled: false },
      tooltip: {
        useHTML: true,
        headerFormat: "",
        outside: false,
        pointFormatter: function () {
          return `<b>${this.dateLabel}</b><br/>Rule changes: <span style="color: #4B5563">${this.z}</span>`;
        },
      },
      plotOptions: {
        series: {
          cursor: "pointer",
          minSize: 20,
          maxSize: 34,
          marker: {
            fillOpacity: 0.8,
            lineWidth: 0,
          },
          dataLabels: {
            enabled: true,
            format: "{point.z}",
            y: 0,
            style: { color: "#eee", textOutline: "none", fontSize: "12px" },
          },
          point: {
            events: {
              click: (e) => {
                const date = dateList[e.point.x];
                this.handleClickDateLabel(date);
              },
            },
          },
        },
      },
      series: [
        {
          data: _.keys(groupedConfig).map((date) => ({
            x: dateIndexMap[date],
            y: 0,
            z: groupedCount[date] || 0,
            dateLabel: moment(date).format("YYYY/MM/DD ddd"),
          })),
          color: "#3B82F6",
        },
      ],
    };

    return chartOptions;
  }

  render() {
    const {
      isLoading,
      isFetching,
      changes,
      filteredChanges,
      isSpecificRule,
      selectedId,
      isOpenView,
      errmsg,
      chartOptions,
      endCursor,
      lastTime,
      hasNextPage,
    } = this.state;

    const {
      timezone,
      handleReplaceData,
      refData,
      groupRule,
      aliasKey,
      currentRule,
      isFirstLook = false,
    } = this.props;

    const selected = _.find(changes, { historyId: selectedId });
    const selectedDiffs = _.get(selected, "diffs", isFirstLook ? {} : []);
    const hasDiffs = isFirstLook
      ? _.sumBy(_.values(selectedDiffs), "length") > 0
      : selectedDiffs.length > 0;

    return (
      <ModalWrapper
        isOpen={true}
        showCloseFooter={true}
        handleClose={this.props.handleClose}
        customOverlayStyle={{ zIndex: 100 }}
        width="75%"
        title={{
          label: "Change Logs ",
          highlight: currentRule
            ? `${currentRule.id} - ${currentRule.name}`
            : "",
        }}
      >
        <div className="relative mb-4 w-full">
          {chartOptions && currentRule && (
            <div className="border rounded mb-3 border-gray-400 px-2 pb-2">
              <HighchartWrapper
                options={chartOptions}
                key={endCursor}
              ></HighchartWrapper>
            </div>
          )}
          <div className="flex h-full w-full gap-4" style={{ height: "540px" }}>
            <div className="relative h-full w-1/3">
              {isLoading && (
                <div
                  className="absolute z-10 flex w-full items-center justify-center"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    height: "calc(100% - 2.3rem)",
                    top: "2.5rem",
                  }}
                >
                  <LoadingUI iconOnly={true}></LoadingUI>
                </div>
              )}
              <div className="mb-1 flex items-center justify-end">
                {hasNextPage ? (
                  <>
                    <div className="mr-auto text-xs text-gray-600">
                      Last loaded:{" "}
                      {lastTime && (
                        <DateTimeFormatter
                          datetime={lastTime}
                          timezone={timezone}
                          place="right"
                        ></DateTimeFormatter>
                      )}
                    </div>
                    {isFetching ? (
                      <button
                        type="button"
                        className={buttonActionClass}
                        onClick={() => this.setState({ isFetching: false })}
                      >
                        Pause Fetching
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`${buttonActionClass} ${
                          isLoading ? "cursor-not-allowed opacity-50" : ""
                        }`}
                        onClick={() => {
                          this.setState({ isFetching: true }, () => {
                            this.handleFetchData();
                          });
                        }}
                        disabled={isLoading}
                      >
                        Load More
                      </button>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-gray-600">
                    All changes data loaded
                  </div>
                )}
              </div>

              <StickyTableWrapper
                className="w-full"
                maxHeight="calc(100% - 2.3rem)"
                handleScroll={this.handleScroll}
                enableScroll={!isLoading && hasNextPage}
              >
                <table className="w-full table-fixed">
                  <thead>
                    <tr>
                      <th className={thClass + " bg-gray-200"}>Log</th>
                      <th className={thClass + " w-16 bg-gray-200"}>Diffs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChanges.map(
                      ({ createdAt, historyId, diffs, notes }) => {
                        const diffCount = _.isPlainObject(diffs)
                          ? _.reduce(
                              _.keys(diffs),
                              (sum, key) => sum + diffs[key].length,
                              0
                            )
                          : diffs.length;
                        return (
                          <HighLightRowWrapper
                            selected={historyId === selectedId}
                            key={historyId}
                            onClick={() => {
                              this.setState({ selectedId: historyId });
                            }}
                            domId={historyId}
                          >
                            <td className={`${tdClass} text-left text-sm`}>
                              <div className="flex items-center gap-1 font-semibold">
                                {historyId}
                              </div>
                              <DateTimeFormatter
                                datetime={createdAt}
                                timezone={timezone}
                                place="right"
                              ></DateTimeFormatter>
                            </td>
                            <td
                              className={`${tdClass} text-center text-sm ${
                                historyId === selectedId ? "font-semibold" : ""
                              }`}
                            >
                              {diffCount === 0 ? "--" : diffCount}
                            </td>
                          </HighLightRowWrapper>
                        );
                      }
                    )}
                  </tbody>
                </table>
                {filteredChanges.length === 0 &&
                  !isLoading &&
                  errmsg === "" && (
                    <div className="mt-2 w-full text-center text-gray-600">
                      No changes found.
                    </div>
                  )}
                {errmsg !== "" && (
                  <div className="mt-2 w-full text-center text-red-600">
                    {errmsg}
                  </div>
                )}
              </StickyTableWrapper>
            </div>

            <div className="flex w-2/3">
              {selected && (
                <>
                  <div className="flex flex-1 flex-col">
                    <div>
                      <div className="mb-1 flex justify-between">
                        <div className=" text-sm font-semibold text-gray-800">
                          {selected.historyId}
                          <DateTimeFormatter
                            datetime={selected.createdAt}
                            timezone={timezone}
                          ></DateTimeFormatter>
                        </div>
                        <div className="flex items-center gap-2">
                          {handleReplaceData && (
                            <button
                              type="button"
                              className={`${buttonClassBlue} ${
                                hasDiffs ? "" : "cursor-not-allowed opacity-50"
                              }`}
                              disabled={!hasDiffs}
                              onClick={() => this.handleReplaceData(selected)}
                            >
                              {isSpecificRule
                                ? "Replace Current Rule"
                                : "Replace Current Config"}
                            </button>
                          )}
                          <button
                            type="button"
                            className={
                              isOpenView ? buttonCloseClass : buttonActionClass
                            }
                            onClick={() =>
                              this.setState({ isOpenView: !isOpenView })
                            }
                          >
                            {isOpenView ? (
                              <FaTimes></FaTimes>
                            ) : (
                              `View ${isSpecificRule ? "Rule" : ""} Config`
                            )}
                          </button>
                        </div>
                      </div>
                      <div className={`${isOpenView ? "mr-2" : ""}`}>
                        <NotesInformation
                          lines={2.5}
                          notes={selected.notes}
                        ></NotesInformation>
                      </div>
                    </div>
                    {selected.compareIdx > -1 && (
                      <div className="py-1 text-sm font-semibold text-gray-600">
                        Compare with previous version
                        <span
                          className="cursor-pointer pl-1 text-blue-600 hover:underline"
                          onClick={() => {
                            this.setState({
                              selectedId:
                                changes[selected.compareIdx].historyId,
                            });
                          }}
                        >
                          {changes[selected.compareIdx].historyId}
                        </span>
                      </div>
                    )}
                    <div
                      className={`${
                        isOpenView ? "mr-2" : ""
                      } border rounded mt-1 flex-1 overflow-y-auto overflow-x-hidden border-gray-500 px-2 py-1`}
                    >
                      {selected.compareIdx > -1 ? (
                        <ConfigDiffView
                          diffs={selected.diffs}
                          groupRule={groupRule}
                          refData={refData}
                          aliasKey={aliasKey}
                          height={"100%"}
                        />
                      ) : (
                        "Initial Config"
                      )}
                    </div>
                  </div>
                  {isOpenView && (
                    <div
                      className="border relative bg-gray-100 p-2 text-xs"
                      style={{ width: "300px" }}
                    >
                      <div
                        className="absolute"
                        style={{ top: "0.5rem", right: "0.5rem" }}
                      >
                        <ClickToCopyButton
                          copyText={JSON.stringify(selected.config)}
                        ></ClickToCopyButton>
                      </div>
                      <div className="h-full overflow-y-auto">
                        <pre>{JSON.stringify(selected.config, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ModalWrapper>
    );
  }
}

export default ChangeLogModal;
